import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { Store, provideStore } from '@ngxs/store';
import { ROLES } from '@shs-digi-care-monorepo/interfaces';
import { provideAuth } from 'angular-auth-oidc-client';
import { provideEchartsCore } from 'ngx-echarts';
import { Observable, of, switchMap, tap, map } from 'rxjs';
import { environment } from '../environments/environment';
import { COMMON_ROUTES } from './common/app.routes';
import { AuthService } from './common/auth/services/auth.service';
import { TokenInjector } from './common/auth/services/token-injector.interceptor';
import { UserService } from './common/auth/services/user.service';
import { EscalationState } from './common/escalation/state/escalation.state';
import { IUser } from './common/shared/interfaces/iUser';
import { FeatureFlag } from './common/shared/models/feature-flag.model';
import { CustomMatPaginatorIntl } from './common/shared/providers/custom-mat-paginator-intl.provider';
import { FeatureFlagService } from './common/shared/services/feature-flag.service';
import { NotificationState } from './common/shared/states/notification/notification.state';
import { ScopeState } from './common/shared/states/scope/scope.state';
import { MDLA_SCOPE, MDLA_SCOPE_ENUM } from './common/shared/tokens/mdla-scope.token';
import { ApplicationState } from './common/store/application/settings.state';
import { FeatureFlagState, FeatureFlags } from './common/store/feature-flag';
import { UserSettings } from './common/store/settings/settings.actions';
import { UserSettingsState } from './common/store/settings/settings.state';
import { TeamActions, TeamState } from './common/store/team';
import { Users, UsersState } from './common/store/user';
import { LOGGING_CONFIG, provideBrowserLocaleId } from '@shs-digi-care-monorepo/frontend-lib';

ModuleRegistry.registerModules([AllCommunityModule]);

// Helper function to create TranslateLoader
export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

// User initialization function
function initUser(store: Store, user: IUser): void {
    store.dispatch(new Users.setMe({ me: user }));
}

function initUserSettings(store: Store): Observable<any> {
    return store.dispatch(new UserSettings.load());
}

// Feature flags initialization function
function initFeatureFlags(featureFlagService: FeatureFlagService, store: Store): Observable<FeatureFlag> {
    return featureFlagService.getFeatureFlags().pipe(tap((flags) => store.dispatch(new FeatureFlags.setFlags(flags))));
}

// Teams initialization function - loads all teams for all streams
function initTeams(store: Store): void {
    store.dispatch(new TeamActions.LoadAll());
}

// App initialization factory function
function initializeAppFactory(
    authService: AuthService,
    userService: UserService,
    featureFlagService: FeatureFlagService,
    store: Store
): () => Observable<boolean> {
    return () =>
        authService.isAuthenticated$.pipe(
            switchMap((isAuthenticated) => {
                if (!isAuthenticated) {
                    /**
                     * If the user is not authenticated, we can skip loading user data and feature flags and just return true to allow the app to initialize
                     * Since the all routes are protected by auth guards,
                     * the user will be redirected to the login page and won't see any protected content until they log in,
                     * at which point the app will re-initialize/bootstrap and load the necessary data
                     */
                    return of(true);
                }

                return initFeatureFlags(featureFlagService, store).pipe(
                    switchMap(() => userService.getMe()),
                    tap((user) => initUser(store, user as IUser)),
                    tap(() => initTeams(store)),
                    switchMap(() => initUserSettings(store)),
                    map(() => true)
                );
            })
        );
}

// ECharts initialization factory
async function initializeEChartsFactory(): Promise<unknown> {
    const [echarts, { BarChart, PieChart }, { TitleComponent, TooltipComponent, GridComponent }, { SVGRenderer }] =
        await Promise.all([
            import('echarts/core'),
            import('echarts/charts'),
            import('echarts/components'),
            import('echarts/renderers')
        ]);

    echarts.use([BarChart, PieChart, TitleComponent, TooltipComponent, GridComponent, SVGRenderer]);
    return echarts;
}

// Application config
export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(COMMON_ROUTES),
        importProvidersFrom(
            TranslateModule.forRoot({
                defaultLanguage: 'en',
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient]
                }
            }),
            NgxsReduxDevtoolsPluginModule.forRoot({ trace: true, disabled: environment.production })
        ),
        provideAuth({
            config: environment.authentication
        }),
        provideEchartsCore({ echarts: initializeEChartsFactory() }),
        provideStore(
            [
                UsersState,
                ApplicationState,
                NotificationState,
                UserSettingsState,
                FeatureFlagState,
                TeamState,
                EscalationState,
                ScopeState
            ],
            {
                developmentMode: !environment.production,
                compatibility: { strictContentSecurityPolicy: true }
            },
            withNgxsStoragePlugin({
                keys: [ApplicationState]
            })
        ),
        provideAppInitializer(() => {
            const initializerFn = initializeAppFactory(
                inject(AuthService),
                inject(UserService),
                inject(FeatureFlagService),
                inject(Store)
            );
            return initializerFn();
        }),
        {
            provide: 'ROLES',
            useValue: ROLES
        },
        {
            provide: 'RESOURCES',
            useValue: []
        },
        {
            provide: 'useScd',
            useValue: true
        },
        {
            provide: MDLA_SCOPE,
            useValue: MDLA_SCOPE_ENUM.NAM // Default scope, can be overridden by environment or other means
        },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInjector, multi: true },
        { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
        { provide: LOGGING_CONFIG, useValue: { stage: environment.stage } },
        provideBrowserLocaleId('en-US'),
        provideNativeDateAdapter(),
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations()
    ]
};
